<template>
  <section class="chart">
    <header class="chart__head">
      <div>
        <h3 class="chart__title">{{ title }}</h3>
        <div v-if="subtitle" class="chart__sub">{{ subtitle }}</div>
      </div>
      <div class="chart__actions">
        <slot name="actions"></slot>
        <button
          v-if="comparable"
          type="button"
          class="cmp"
          :class="{ 'is-on': comparing }"
          :disabled="loading"
          :title="comparing ? 'Sakrij prethodni period' : 'Prikaži prethodni period' + (compareHint ? ': ' + compareHint : '')"
          @click="$emit('toggle')"
        >
          <i :class="loading ? 'el-icon-loading' : 'el-icon-data-line'"></i>
          Prethodni period
        </button>
      </div>
    </header>
    <div class="chart__body" :style="{ height: height + 'px' }">
      <canvas ref="canvas"></canvas>
      <div v-if="isEmpty" class="chart__empty">Nema podataka za odabrani period</div>
    </div>
  </section>
</template>

<script>
import Chart from "chart.js/auto";

// Chart.js defaults aligned with the app's type and ink tokens.
Chart.defaults.font.family =
  "Roboto, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
Chart.defaults.font.size = 11;
Chart.defaults.color = "#64748b";

export default {
  name: "ChartCard",
  props: {
    title: { type: String, default: "" },
    subtitle: { type: String, default: "" },
    type: { type: String, default: "bar" },
    data: { type: Object, required: true },
    options: { type: Object, default: () => ({}) },
    height: { type: Number, default: 260 },
    money: { type: Boolean, default: false },
    // Opt-in "compare with the previous period" control in the header.
    comparable: { type: Boolean, default: false },
    comparing: { type: Boolean, default: false },
    compareHint: { type: String, default: "" },
    loading: { type: Boolean, default: false },
  },
  data() {
    return { chart: null };
  },
  computed: {
    // With indexAxis 'y' the category labels live on y and the values on x,
    // so the numeric formatter has to follow the value axis — otherwise the
    // categories render as their indexes (0, 1, 2...).
    horizontal() {
      return (this.options || {}).indexAxis === "y";
    },
    valueAxis() {
      return this.horizontal ? "x" : "y";
    },
    categoryAxis() {
      return this.horizontal ? "y" : "x";
    },
    isEmpty() {
      const sets = (this.data && this.data.datasets) || [];
      return !sets.length || sets.every((d) => !(d.data || []).some((v) => v));
    },
  },
  watch: {
    data: {
      deep: true,
      handler() {
        this.render();
      },
    },
  },
  mounted() {
    this.render();
  },
  beforeDestroy() {
    if (this.chart) this.chart.destroy();
  },
  methods: {
    fmt(v) {
      const n = Number(v) || 0;
      const s = this.money ? n.toFixed(2) : String(Math.round(n));
      const parts = s.split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      return parts.join(",") + (this.money ? " KM" : "");
    },
    axisTick(v) {
      const n = Number(v) || 0;
      if (!this.money) return Math.round(n);
      if (Math.abs(n) >= 1000000) return (n / 1000000).toFixed(1) + "M";
      if (Math.abs(n) >= 1000) return Math.round(n / 1000) + "k";
      return n;
    },
    baseOptions() {
      const self = this;
      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        // keep grouped bars adjacent instead of drifting apart in wide slots
        categoryPercentage: 0.62,
        barPercentage: 0.92,
        plugins: {
          legend: {
            display: (this.data.datasets || []).length > 1,
            position: "top",
            align: "end",
            labels: { boxWidth: 9, boxHeight: 9, usePointStyle: true, pointStyle: "rectRounded", padding: 14 },
          },
          tooltip: {
            backgroundColor: "#17222e",
            padding: 10,
            cornerRadius: 6,
            titleFont: { weight: "600" },
            displayColors: true,
            boxWidth: 9,
            boxHeight: 9,
            usePointStyle: true,
            callbacks: {
              label(ctx) {
                const label = ctx.dataset.label ? ctx.dataset.label + ": " : "";
                return label + self.fmt(ctx.parsed[self.valueAxis]);
              },
            },
          },
        },
        scales: {
          [this.categoryAxis]: {
            grid: { display: false },
            border: { color: "#e2e8f0" },
            ticks: {
              maxRotation: 0,
              autoSkipPadding: 12,
              // keep the category's own text, never its index
              callback(value) {
                return this.getLabelForValue(value);
              },
            },
          },
          [this.valueAxis]: {
            beginAtZero: true,
            grid: { color: "#f1f5f9", drawTicks: false },
            border: { display: false },
            ticks: { padding: 8, callback: (v) => self.axisTick(v) },
          },
        },
      };
    },
    merge(a, b) {
      const out = { ...a };
      Object.keys(b || {}).forEach((k) => {
        out[k] =
          b[k] && typeof b[k] === "object" && !Array.isArray(b[k])
            ? this.merge(a[k] || {}, b[k])
            : b[k];
      });
      return out;
    },
    render() {
      if (this.chart) this.chart.destroy();
      const ctx = this.$refs.canvas;
      if (!ctx) return;
      this.chart = new Chart(ctx, {
        type: this.type,
        data: this.data,
        options: this.merge(this.baseOptions(), this.options),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.chart {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  padding: 16px 18px;
  min-width: 0;
}

.chart__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.cmp {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: var(--surface);
  border: 1px solid var(--line-strong);
  border-radius: 999px;
  font: inherit;
  font-size: var(--fs-xs);
  color: var(--ink-2);
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    border-color: var(--brand);
    color: var(--brand-ink);
  }

  &.is-on {
    background: var(--brand-tint);
    border-color: var(--brand-line);
    color: var(--brand-ink);
    font-weight: 600;
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
}

.chart__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.chart__title {
  font-size: var(--fs-lg);
  font-weight: 600;
  color: var(--ink);
  margin: 0;
}

.chart__sub {
  font-size: var(--fs-sm);
  color: var(--ink-4);
  margin-top: 2px;
}

.chart__body {
  position: relative;
}

.chart__empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--fs-md);
  color: var(--ink-4);
  background: var(--surface);
}
</style>
